import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  useMediaQuery,
  Dialog,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade,
  Paper,
  Button,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  DarkMode,
  LightMode,
  Search as SearchIcon,
  Person as PersonIcon,
  Groups as TeamsIcon,
  AutoAwesome as DashboardIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  SportsBaseball,
  Whatshot,
  Leaderboard,
  Casino,
  SwapHoriz,
  EmojiEvents,
  WorkspacePremium,
  Settings as SettingsIcon,
  Logout,
  Login,
  MenuBook,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import logo from '../../logo.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { playersApi } from '../../services/apiService';
import { AuthModal } from '../AuthModal';
import { getTeamLogoUrl } from '../../utils/teamLogos';
import { useAuth } from '../../context/AuthContext';

// All MLB teams for search
const ALL_TEAMS = [
  { abbr: 'ARI', name: 'Arizona Diamondbacks', division: 'NL West' },
  { abbr: 'ATL', name: 'Atlanta Braves', division: 'NL East' },
  { abbr: 'BAL', name: 'Baltimore Orioles', division: 'AL East' },
  { abbr: 'BOS', name: 'Boston Red Sox', division: 'AL East' },
  { abbr: 'CHC', name: 'Chicago Cubs', division: 'NL Central' },
  { abbr: 'CWS', name: 'Chicago White Sox', division: 'AL Central' },
  { abbr: 'CIN', name: 'Cincinnati Reds', division: 'NL Central' },
  { abbr: 'CLE', name: 'Cleveland Guardians', division: 'AL Central' },
  { abbr: 'COL', name: 'Colorado Rockies', division: 'NL West' },
  { abbr: 'DET', name: 'Detroit Tigers', division: 'AL Central' },
  { abbr: 'HOU', name: 'Houston Astros', division: 'AL West' },
  { abbr: 'KC', name: 'Kansas City Royals', division: 'AL Central' },
  { abbr: 'LAA', name: 'Los Angeles Angels', division: 'AL West' },
  { abbr: 'LAD', name: 'Los Angeles Dodgers', division: 'NL West' },
  { abbr: 'MIA', name: 'Miami Marlins', division: 'NL East' },
  { abbr: 'MIL', name: 'Milwaukee Brewers', division: 'NL Central' },
  { abbr: 'MIN', name: 'Minnesota Twins', division: 'AL Central' },
  { abbr: 'NYM', name: 'New York Mets', division: 'NL East' },
  { abbr: 'NYY', name: 'New York Yankees', division: 'AL East' },
  { abbr: 'OAK', name: 'Oakland Athletics', division: 'AL West' },
  { abbr: 'PHI', name: 'Philadelphia Phillies', division: 'NL East' },
  { abbr: 'PIT', name: 'Pittsburgh Pirates', division: 'NL Central' },
  { abbr: 'SD', name: 'San Diego Padres', division: 'NL West' },
  { abbr: 'SF', name: 'San Francisco Giants', division: 'NL West' },
  { abbr: 'SEA', name: 'Seattle Mariners', division: 'AL West' },
  { abbr: 'STL', name: 'St. Louis Cardinals', division: 'NL Central' },
  { abbr: 'TB', name: 'Tampa Bay Rays', division: 'AL East' },
  { abbr: 'TEX', name: 'Texas Rangers', division: 'AL West' },
  { abbr: 'TOR', name: 'Toronto Blue Jays', division: 'AL East' },
  { abbr: 'WSH', name: 'Washington Nationals', division: 'NL East' },
];

// Searchable pages
const PAGES = [
  { name: 'Cycle IQ', path: '/', icon: DashboardIcon, keywords: ['home', 'ask', 'ai', 'search', 'question', 'iq', 'dashboard'] },
  { name: 'Players', path: '/players', icon: PersonIcon, keywords: ['roster', 'batting', 'pitching'] },
  { name: 'Teams', path: '/teams', icon: TeamsIcon, keywords: ['roster', 'franchise'] },
  { name: 'Standings', path: '/standings', icon: EmojiEvents, keywords: ['rankings', 'division', 'record'] },
  { name: 'Scores', path: '/scores', icon: SportsBaseball, keywords: ['live', 'game', 'boxscore'] },
  { name: 'Pitch Lab', path: '/pitch-lab', icon: Leaderboard, keywords: ['pitch', 'velocity', 'movement', 'spin', 'stuff', 'lab'] },
  { name: 'At-Bat Explorer', path: '/at-bat-explorer', icon: SportsBaseball, keywords: ['at-bat', 'atbat', 'pitch by pitch', 'zone', 'at bat'] },
  { name: 'Awards Tracker', path: '/awards', icon: EmojiEvents, keywords: ['mvp', 'cy young', 'roy', 'rookie', 'award', 'race'] },
  // Pro
  { name: 'Fantasy / DFS ★', path: '/fantasy', icon: Casino, keywords: ['draftkings', 'fanduel', 'dfs', 'lineup', 'pro'] },
  { name: 'Trade Value Engine ★', path: '/trades', icon: SwapHoriz, keywords: ['trade', 'value', 'mock', 'dtv', 'cts', 'cvr', 'pro'] },
  { name: 'Hot / Cold Streaks ★', path: '/streaks', icon: Whatshot, keywords: ['streak', 'trending', 'hot', 'cold', 'pro'] },
];

// Recent searches (localStorage)
const RECENT_KEY = 'theCycle-recentSearches';
const MAX_RECENT = 6;

function getRecentSearches() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function addRecentSearch(item) {
  const recent = getRecentSearches().filter(r => r.path !== item.path);
  recent.unshift(item);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

// Route → breadcrumb label mapping
const ROUTE_LABELS = {
  '/': 'Cycle IQ',
  '/players': 'Players',
  '/teams': 'Teams',
  '/standings': 'Standings',
  '/scores': 'Scores',
  '/leaders': 'Leaders',
  '/explorer': 'Explorer',
  '/glossary': 'Glossary',
  '/compare-teams': 'Compare Teams',
  '/trades': 'Trades',
  '/fantasy': 'Fantasy / DFS',
  '/streaks': 'Hot / Cold Streaks',
  '/salaries': 'Salary Explorer',
  '/awards': 'Awards Tracker',
  '/settings': 'Settings',
};

function getPageLabel(pathname) {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  if (pathname.startsWith('/players/profile/')) return 'Player Detail';
  if (pathname.startsWith('/players/')) return 'Player Detail';
  if (pathname.startsWith('/teams/')) return 'Team Detail';
  if (pathname.startsWith('/boxscore/')) return 'Box Score';
  return null;
}

const Navigation = ({ 
  darkMode, 
  onToggleDarkMode, 
  onToggleSidebar, 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery('(min-width:900px)');
  const inputRef = useRef(null);
  const { isPaid, user, startCheckout, logout, openPortal } = useAuth();

  // Account menu state
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Command palette state
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ pages: [], teams: [], players: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState(getRecentSearches);
  const searchTimerRef = useRef(null);

  // Breadcrumb label for current page
  const pageLabel = useMemo(() => getPageLabel(location.pathname), [location.pathname]);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults({ pages: [], teams: [], players: [] });
      setSelectedIndex(0);
      setRecentSearches(getRecentSearches());
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Search logic — pages + teams are instant, players are debounced API call
  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    setSelectedIndex(0);

    if (!value || value.length < 1) {
      setResults({ pages: [], teams: [], players: [] });
      return;
    }

    const q = value.toLowerCase();

    // Instant: filter pages
    const matchedPages = PAGES.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.keywords.some(k => k.includes(q))
    ).slice(0, 4);

    // Instant: filter teams
    const matchedTeams = ALL_TEAMS.filter(t =>
      t.name.toLowerCase().includes(q) ||
      t.abbr.toLowerCase().includes(q) ||
      t.division.toLowerCase().includes(q)
    ).slice(0, 5);

    setResults(prev => ({ ...prev, pages: matchedPages, teams: matchedTeams }));

    // Debounced: API player search
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length >= 2) {
      setLoading(true);
      searchTimerRef.current = setTimeout(async () => {
        try {
          const result = await playersApi.searchUnified(value, { limit: 8 });
          const players = (result?.results || result?.players || []).slice(0, 8).map(p => ({
            name: p.name || p.player || 'Unknown',
            team: p.currentTeam || p.team || '',
            position: p.position || '',
            slug: p.slug || (p.name || '').toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-]/g, ''),
          }));
          setResults(prev => ({ ...prev, players }));
        } catch {
          setResults(prev => ({ ...prev, players: [] }));
        } finally {
          setLoading(false);
        }
      }, 250);
    } else {
      setResults(prev => ({ ...prev, players: [] }));
      setLoading(false);
    }
  }, []);

  // Flatten results for keyboard navigation
  const allResults = useMemo(() => {
    const items = [];
    results.pages.forEach(p => items.push({ type: 'page', ...p }));
    results.teams.forEach(t => items.push({ type: 'team', ...t }));
    results.players.forEach(p => items.push({ type: 'player', ...p }));
    if (!query && recentSearches.length > 0) {
      recentSearches.forEach(r => items.push({ origType: r.type, ...r, type: 'recent' }));
    }
    return items;
  }, [results, query, recentSearches]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIndex]) {
      e.preventDefault();
      selectItem(allResults[selectedIndex]);
    }
  }, [allResults, selectedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectItem = useCallback((item) => {
    let path, label;
    if (item.type === 'page') {
      path = item.path;
      label = item.name;
    } else if (item.type === 'team') {
      path = `/teams/${item.abbr}`;
      label = item.name;
    } else if (item.type === 'player') {
      path = `/players/profile/${item.slug}`;
      label = item.name;
    } else if (item.type === 'recent') {
      path = item.path;
      label = item.label;
    }
    if (path) {
      addRecentSearch({ path, label, type: item.type === 'recent' ? item.origType : item.type });
      navigate(path);
      setOpen(false);
    }
  }, [navigate]);

  const isMac = useMemo(() => typeof navigator !== 'undefined' && /Mac/.test(navigator.platform), []);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backdropFilter: 'blur(20px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Menu button — hidden on desktop (persistent sidebar) */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={onToggleSidebar}
            sx={{ mr: 2, display: isDesktop ? 'none' : 'inline-flex' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo and Brand */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mr: 4
              }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src={logo}
                alt="The Cycle"
                sx={{
                  width: { xs: 44, sm: 56 },
                  height: { xs: 44, sm: 56 },
                  mr: { xs: 0, sm: 2 },
                }}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  The Cycle
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  Beyond the Box Score.
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {/* Spacer */}
          <Box sx={{ mr: 'auto' }} />

          {/* Search trigger — opens command palette */}
          <Tooltip title={`Search (${isMac ? '⌘' : 'Ctrl'}+K)`}>
            <Box
              onClick={() => setOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: { xs: 1.5, sm: 2 },
                py: 0.75,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: alpha(theme.palette.background.default, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: alpha(theme.palette.background.default, 0.9),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                minWidth: { xs: 'auto', sm: 200, md: 260 },
              }}
            >
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' }, flex: 1 }}
              >
                Search players, teams...
              </Typography>
              <Chip
                label={isMac ? '⌘K' : 'Ctrl+K'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.divider, 0.15),
                  display: { xs: 'none', md: 'flex' },
                }}
              />
            </Box>
          </Tooltip>

          {/* Right side controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            {/* Glossary — opens the global glossary dialog */}
            <Tooltip title={`Stats Glossary (${isMac ? '⌘' : 'Ctrl'}+G)`}>
              <IconButton
                color="inherit"
                onClick={() => window.dispatchEvent(new CustomEvent('tc-open-glossary'))}
                sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}
              >
                <MenuBook fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Upgrade to Pro button — desktop only, not paid */}
            {!isPaid && user && (
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<WorkspacePremium />}
                  onClick={async () => { try { await startCheckout(); } catch (_) {} }}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    fontSize: '0.8rem',
                    py: 0.6,
                    background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
                    '&:hover': { background: 'linear-gradient(135deg, #162f5a, #c96520)' },
                  }}
                >
                  Upgrade to Pro
                </Button>
              </motion.div>
            )}

            {/* Theme toggle */}
            <Tooltip title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  color="inherit"
                  onClick={onToggleDarkMode}
                  sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}
                >
                  {darkMode ? <LightMode /> : <DarkMode />}
                </IconButton>
              </motion.div>
            </Tooltip>

            {/* Account button */}
            <Tooltip title={user ? user.email : 'Sign in'}>
              <IconButton
                onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                size="small"
                sx={{
                  p: 0.25,
                  border: `2px solid ${user ? (isPaid ? theme.palette.warning.main : alpha(theme.palette.divider, 0.4)) : alpha(theme.palette.divider, 0.3)}`,
                  borderRadius: '50%',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    bgcolor: user
                      ? (isPaid ? 'warning.main' : 'primary.main')
                      : alpha(theme.palette.text.secondary, 0.15),
                    color: user ? 'white' : 'text.secondary',
                  }}
                >
                  {user ? user.email[0].toUpperCase() : <PersonIcon sx={{ fontSize: 16 }} />}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* Account dropdown menu */}
            <Menu
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={() => setAccountMenuAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              slotProps={{
                paper: {
                  elevation: 3,
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    overflow: 'visible',
                  },
                },
              }}
            >
              {user ? [
                /* Signed-in header */
                <Box key="header" sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem', mb: 0.25 }}>Signed in as</Typography>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>{user.email}</Typography>
                  <Chip
                    label={isPaid
                      ? (user.proDaysLeft != null ? `Pro · ${user.proDaysLeft}d left` : 'Pro')
                      : 'Free'}
                    size="small"
                    sx={{
                      mt: 0.5,
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: isPaid ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.divider, 0.2),
                      color: isPaid ? 'warning.main' : 'text.secondary',
                    }}
                  />
                </Box>,
                <Divider key="div1" />,
                <MenuItem key="settings" onClick={() => { navigate('/settings'); setAccountMenuAnchor(null); }} sx={{ gap: 1.5, py: 1 }}>
                  <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">Settings</Typography>
                </MenuItem>,
                isPaid
                  ? <MenuItem key="portal" onClick={async () => { setAccountMenuAnchor(null); try { await openPortal(); } catch (_) {} }} sx={{ gap: 1.5, py: 1 }}>
                      <WorkspacePremium fontSize="small" sx={{ color: 'warning.main' }} />
                      <Typography variant="body2">Manage Subscription</Typography>
                    </MenuItem>
                  : <MenuItem key="upgrade" onClick={async () => { setAccountMenuAnchor(null); try { await startCheckout(); } catch (_) {} }} sx={{ gap: 1.5, py: 1 }}>
                      <WorkspacePremium fontSize="small" sx={{ color: 'primary.main' }} />
                      <Typography variant="body2" color="primary.main" fontWeight={600}>Upgrade to Pro</Typography>
                    </MenuItem>,
                <Divider key="div2" />,
                <MenuItem key="logout" onClick={() => { logout(); setAccountMenuAnchor(null); }} sx={{ gap: 1.5, py: 1, color: 'error.main' }}>
                  <Logout fontSize="small" />
                  <Typography variant="body2" color="error.main">Sign out</Typography>
                </MenuItem>,
              ] : [
                /* Signed-out state */
                <Box key="anon" sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">You're not signed in</Typography>
                </Box>,
                <Divider key="div1" />,
                <MenuItem key="signin" onClick={() => { setAccountMenuAnchor(null); setAuthModalOpen(true); }} sx={{ gap: 1.5, py: 1 }}>
                  <Login fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" color="primary.main" fontWeight={600}>Sign in / Register</Typography>
                </MenuItem>,
                <MenuItem key="settings" onClick={() => { navigate('/settings'); setAccountMenuAnchor(null); }} sx={{ gap: 1.5, py: 1 }}>
                  <SettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">Settings</Typography>
                </MenuItem>,
              ]}
            </Menu>

            {/* Auth modal (login/register) */}
            <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Command Palette Dialog ── */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={150}
        PaperProps={{
          sx: {
            mt: { xs: 2, sm: '10vh' },
            mx: { xs: 1, sm: 'auto' },
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            maxHeight: '70vh',
          },
        }}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)', bgcolor: alpha('#000', 0.4) } } }}
      >
        {/* Search input */}
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
          <TextField
            inputRef={inputRef}
            fullWidth
            variant="standard"
            placeholder="Search players, teams, or pages..."
            value={query}
            onChange={e => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            InputProps={{ disableUnderline: true }}
            sx={{ '& input': { fontSize: '1.1rem', fontWeight: 500 } }}
          />
          {loading && <CircularProgress size={18} sx={{ mr: 0.5 }} />}
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* Results */}
        <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
          {/* Empty state — show recent searches */}
          {!query && recentSearches.length > 0 && (
            <>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                Recent Searches
              </Typography>
              <List dense disablePadding>
                {recentSearches.map((item, i) => (
                  <ListItemButton
                    key={item.path}
                    selected={selectedIndex === i}
                    onClick={() => selectItem({ ...item, type: 'recent', origType: item.type })}
                    sx={{ px: 2, py: 0.8 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {/* No query, no recent */}
          {!query && recentSearches.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Type to search players, teams, or navigate to any page
              </Typography>
            </Box>
          )}

          {/* Pages */}
          {results.pages.length > 0 && (
            <>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                Pages
              </Typography>
              <List dense disablePadding>
                {results.pages.map((page, i) => {
                  const idx = i;
                  const Icon = page.icon;
                  return (
                    <ListItemButton
                      key={page.path}
                      selected={selectedIndex === idx}
                      onClick={() => selectItem({ type: 'page', ...page })}
                      sx={{ px: 2, py: 0.8 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={page.name}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      />
                      <Chip label="Page" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
                    </ListItemButton>
                  );
                })}
              </List>
            </>
          )}

          {/* Teams */}
          {results.teams.length > 0 && (
            <>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                Teams
              </Typography>
              <List dense disablePadding>
                {results.teams.map((team, i) => {
                  const idx = results.pages.length + i;
                  return (
                    <ListItemButton
                      key={team.abbr}
                      selected={selectedIndex === idx}
                      onClick={() => selectItem({ type: 'team', ...team })}
                      sx={{ px: 2, py: 0.8 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          component="img"
                          src={getTeamLogoUrl(team.abbr)}
                          alt={team.abbr}
                          sx={{ width: 24, height: 24 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={team.name}
                        secondary={team.division}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                      <Chip label={team.abbr} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                    </ListItemButton>
                  );
                })}
              </List>
            </>
          )}

          {/* Players */}
          {results.players.length > 0 && (
            <>
              <Typography variant="overline" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                Players
              </Typography>
              <List dense disablePadding>
                {results.players.map((player, i) => {
                  const idx = results.pages.length + results.teams.length + i;
                  return (
                    <ListItemButton
                      key={player.slug + i}
                      selected={selectedIndex === idx}
                      onClick={() => selectItem({ type: 'player', ...player })}
                      sx={{ px: 2, py: 0.8 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {player.team ? (
                          <Box
                            component="img"
                            src={getTeamLogoUrl(player.team)}
                            alt={player.team}
                            sx={{ width: 22, height: 22 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={player.name}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {player.position && (
                          <Chip label={player.position} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                        {player.team && (
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {player.team}
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            </>
          )}

          {/* No results */}
          {query && query.length >= 2 && !loading && results.pages.length === 0 && results.teams.length === 0 && results.players.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No results for "{query}"
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer hint */}
        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            <kbd style={{ fontFamily: 'inherit', fontWeight: 600, padding: '1px 4px', border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, borderRadius: 3, fontSize: '0.65rem' }}>↑↓</kbd> navigate
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            <kbd style={{ fontFamily: 'inherit', fontWeight: 600, padding: '1px 4px', border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, borderRadius: 3, fontSize: '0.65rem' }}>↵</kbd> select
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            <kbd style={{ fontFamily: 'inherit', fontWeight: 600, padding: '1px 4px', border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, borderRadius: 3, fontSize: '0.65rem' }}>esc</kbd> close
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default Navigation;
